import { sample } from 'effector';
import { condition, spread } from 'patronum';
import { getZeroDataFx } from './effects.js';
import {
  putIndexCardsDataEv,
  putPublicCardsDataEv,
} from '../indexCardsModel/index.js';
import {
  calculateIndexCardsByHexEv,
  changeAttractLayerEv,
  changeInfoModalEv,
  changePublicBusinessTypeEv,
  initGradientByPublicEv,
  manageGradientEv,
  putZoom7HexagonsEv,
  recalculatePublicCardsEv,
  resetChatPublicMessagesEv,
  resetChosenZoom7HexagonEv,
  resetClickCoordsEv,
  resetInfoModalEv,
  setChatPublicMessagesEv,
  setChosenZoom7HexagonEv,
  setClickCoordsEv,
  setRangeGradientEv,
  setRegularGradientEv,
  toggleStopsEv,
} from './events.js';
import {
  $attractLayer,
  $chatPublicMessages,
  $chosenZoom7Hexagon,
  $clickCoords,
  $infoModal,
  $publicBusinessType,
  $showStops,
  $zoom7Hexagons,
  $zoom7StartHexagons,
} from './stores.js';
import {
  calculateThresholdsEv,
  putThresholdsEv,
} from '../gradientModel/index.js';
import {
  $activeFilters,
  $publicFilters,
  debouncePublicFiltersEv,
  initPublicFiltersEv,
} from '../activeFiltersModel/index.js';
import { calculateIndexCards, filterPublicHex } from './utils.js';
import filterHexagons from '../../counters/hex_counter/hex_counter.js';
import { delayedHideLoaderEv } from '../webSocketModel/index.js';
import {
  $activeLitePreset,
  $isPreset,
  $liteBusiness,
} from '../rbpLiteModel/index.js';
import {
  $chatPublicHexagons,
  $isChatPublicFilters,
  applyChatPublicHexagonsEv,
  revertChatPublicFiltersEv,
} from '../chatPublicModel/index.js';

$chosenZoom7Hexagon.reset(resetChosenZoom7HexagonEv);

$publicBusinessType.on(changePublicBusinessTypeEv, (_, payload) => payload);

$clickCoords.reset(resetClickCoordsEv);

$showStops.on(toggleStopsEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

$attractLayer.on(changeAttractLayerEv, (state, payload) => {
  if (state === payload) return null;
  return payload;
});

$chatPublicMessages
  .on(setChatPublicMessagesEv, (state, payload) => payload)
  .reset(resetChatPublicMessagesEv);

$infoModal
  .on(changeInfoModalEv, (state, payload) => payload)
  .reset(resetInfoModalEv);

sample({
  clock: getZeroDataFx.doneData,
  fn: (clock) => clock.city_total,
  target: putPublicCardsDataEv,
});

sample({
  clock: getZeroDataFx.doneData,
  fn: (clock) => clock.polygons,
  target: [putZoom7HexagonsEv, initPublicFiltersEv],
});

sample({
  clock: putZoom7HexagonsEv,
  fn: (clock) =>
    clock.map((item, index) => {
      return {
        ...item,
        id: index,
        properties: {
          ...item.properties,
          index_main: item.properties.competitors_cnt_B1,
        },
      };
    }),
  target: [$zoom7Hexagons, $zoom7StartHexagons, initGradientByPublicEv],
});

sample({
  clock: initGradientByPublicEv,
  filter: () => window.location.pathname === '/public',
  fn: (clock) => clock,
  target: calculateThresholdsEv,
});

// sample({
//   source: [$zoom7Hexagons, $liteBusiness],
//   clock: $isPreset.updates,
//   filter: (source, clock) => !clock,
//   fn: ([hex, liteBusiness]) => {
//     return hex.map((item) => {
//       return {
//         ...item,
//         properties: {
//           ...item.properties,
//           index_main:
//             item.properties[`competitors_cnt_B${liteBusiness.business}`],
//         },
//       };
//     });
//   },
//   target: [$zoom7Hexagons, calculateThresholdsEv],
// });

sample({
  source: $zoom7Hexagons,
  clock: $isPreset.updates,
  filter: (source, clock) => !clock,
  fn: (source) => source,
  target: calculateThresholdsEv,
});

sample({
  source: $liteBusiness,
  clock: $isPreset.updates,
  filter: (source, clock) => !clock,
  fn: (source) => `competitors_cnt_B${source.business || 1}`,
  target: $publicBusinessType,
});
sample({
  source: $chosenZoom7Hexagon,
  clock: setChosenZoom7HexagonEv,
  fn: (source, clock) => {
    if (source && source.id === clock.id) {
      return null;
    }
    return clock;
  },
  target: [$chosenZoom7Hexagon, calculateIndexCardsByHexEv],
});

sample({
  source: $clickCoords,
  clock: setClickCoordsEv,
  fn: (source, clock) => {
    if (source && source.id === clock.id) {
      return null;
    }
    return clock;
  },
  target: $clickCoords,
});

sample({
  source: [$zoom7StartHexagons, $publicBusinessType, $isPreset, $publicFilters],
  clock: $activeFilters.updates,
  filter: ([_, __, isPreset]) =>
    window.location.pathname !== '/tezber' && !isPreset,
  fn: ([hexagons, business_type, _, publicFilters], clock) => {
    const chosen_metric = clock.chosen_metrics[0];
    let mutatedHexagons = hexagons.map((item) => {
      let prop = chosen_metric;
      if (chosen_metric === 'competitors_cnt_B') prop = business_type;
      if (chosen_metric === 'provision_B')
        prop = `provision_B${business_type.slice(17)}`;
      return {
        ...item,
        properties: {
          ...item.properties,
          index_main: item.properties[prop],
        },
      };
    });

    mutatedHexagons = filterPublicHex(
      mutatedHexagons,
      publicFilters,
      business_type,
      chosen_metric
    );

    if (clock.gradient.length > 0) {
      return {
        activeFilters: clock,
        hexagons: filterHexagons(mutatedHexagons, clock, [], 'index'),
      };
    }
    return {
      activeFilters: clock,
      hexagons: mutatedHexagons,
    };
  },
  target: manageGradientEv,
});

sample({
  source: [$zoom7StartHexagons, $activeFilters],
  clock: $publicBusinessType.updates,
  filter: ([_, activeFilters]) =>
    activeFilters.chosen_metrics[0] === 'competitors_cnt_B' ||
    activeFilters.chosen_metrics[0] === 'provision_B',
  fn: ([hexagons, activeFilters], clock) => {
    let prop;
    if (activeFilters.chosen_metrics[0] === 'competitors_cnt_B') prop = clock;
    if (activeFilters.chosen_metrics[0] === 'provision_B')
      prop = `provision_B${clock.slice(17)}`;
    return {
      activeFilters,
      hexagons: hexagons.map((item) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            index_main: item.properties[prop],
          },
        };
      }),
    };
  },
  target: manageGradientEv,
});

condition({
  source: manageGradientEv,
  if: ({ activeFilters }) => {
    return (
      activeFilters.chosen_metrics[0] === 'income_avg_range' ||
      activeFilters.chosen_metrics[0] === 'rent_price_range'
    );
  },
  then: setRangeGradientEv,
  else: setRegularGradientEv,
});

sample({
  clock: setRegularGradientEv,
  fn: (clock) => {
    return clock.hexagons;
  },
  target: [$zoom7Hexagons, calculateThresholdsEv],
});

sample({
  clock: setRangeGradientEv,
  fn: (clock) => {
    return {
      gradient: [0, 1, 2, 3, 5, 6],
      hexagons: clock.hexagons,
    };
  },
  target: spread({
    targets: {
      gradient: putThresholdsEv,
      hexagons: $zoom7Hexagons,
    },
  }),
});

sample({
  source: [$zoom7Hexagons, $chosenZoom7Hexagon],
  clock: $publicBusinessType.updates,
  fn: ([hexagons, chosen_hex], business_type) => {
    if (chosen_hex) {
      return calculateIndexCards([chosen_hex], business_type);
    }
    return calculateIndexCards(hexagons, business_type);
  },
  target: putPublicCardsDataEv,
});

sample({
  source: $publicBusinessType,
  clock: calculateIndexCardsByHexEv,
  filter: (source, clock) => clock,
  fn: (business_type, clock) => calculateIndexCards([clock], business_type),
  target: putPublicCardsDataEv,
});

sample({
  source: [$zoom7Hexagons, $publicBusinessType],
  clock: calculateIndexCardsByHexEv,
  filter: (source, clock) => !clock,
  fn: ([hexagons, business_type], clock) =>
    calculateIndexCards(hexagons, business_type),
  target: putPublicCardsDataEv,
});

// sample({
//   source: $chosenZoom7Hexagon,
//   clock: $zoom7Hexagons.updates,
//   filter: (source) => source,
//   fn: (source, clock) => source.find((item) => item.id === clock.id),
//   target: $chosenZoom7Hexagon,
// });

sample({
  source: [$zoom7StartHexagons, $publicBusinessType],
  clock: resetChosenZoom7HexagonEv,
  fn: ([hexagons, business_type], _) => {
    return calculateIndexCards(hexagons, business_type);
  },
  target: putPublicCardsDataEv,
});

sample({
  clock: $activeLitePreset.updates,
  fn: (clock) => {
    // return `t${clock.business}pt${clock.sub_business}`;
    return `competitors_cnt_B${clock.sub_business}`;
  },
  target: $publicBusinessType,
});

sample({
  source: [$zoom7StartHexagons, $publicBusinessType, $activeFilters, $isPreset],
  clock: debouncePublicFiltersEv,
  filter: ([_, __, ___, isPreset]) =>
    window.location.pathname === '/public' || !isPreset,
  fn: ([startHex, business_type, activeFilters], clock) => {
    return filterPublicHex(
      startHex,
      clock,
      business_type,
      activeFilters.chosen_metrics[0]
    );
  },
  target: [$zoom7Hexagons, calculateThresholdsEv],
});

sample({
  source: [$zoom7StartHexagons, $chatPublicHexagons],
  clock: applyChatPublicHexagonsEv,
  fn: ([startData, chatHexagons], activeMessage) => {
    return startData.filter((item) => {
      return chatHexagons[activeMessage].includes(item.properties.Zoom8_id);
    });
  },
  target: $zoom7Hexagons,
});

sample({
  source: [$zoom7StartHexagons, $activeFilters, $publicBusinessType],
  clock: revertChatPublicFiltersEv,
  fn: ([startData, activeFilters, business_type]) => {
    const chosen_metric = activeFilters.chosen_metrics[0];
    return startData.map((item) => {
      let prop = chosen_metric;
      if (chosen_metric === 'competitors_cnt_B') prop = business_type;
      if (chosen_metric === 'provision_B')
        prop = `provision_B${business_type.slice(17)}`;
      return {
        ...item,
        properties: {
          ...item.properties,
          index_main: item.properties[prop],
        },
      };
    });
  },
  target: $zoom7Hexagons,
});

sample({
  source: $isChatPublicFilters,
  clock: $activeFilters.updates,
  filter: (isChatFilters, _) => isChatFilters,
  fn: () => false,
  target: $isChatPublicFilters,
});
